import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Building, Calendar, Clock, User, Users } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { useMediaQuery } from "@mui/material";
import { getFormatedDate } from "../../services/helpers";

const NotificationDetails = (props) => {
  const [notification, setNotificationData] = useState({});
  const [showAllRecipients, setShowAllRecipients] = useState(false);
  const isMobile = useMediaQuery("(max-width:767px)");

  const MethodBadges = ({ methods }) => {
    let normalized = [];

    if (Array.isArray(methods)) {
      normalized = methods;
    } else if (methods === "Both") {
      normalized = ["Email", "Push", "SMS"];
    } else if (methods === "PushNotification") {
      normalized = ["Push"];
    } else if (methods) {
      normalized = [methods];
    }

    const allTypes = ["Email", "Push", "SMS"];
    const hasAll = allTypes.every((type) => normalized.includes(type));

    if (hasAll) {
      return (
        <Badge
          variant="info"
          className="whitespace-nowrap"
          style={{ borderRadius: "6px" }}
        >
          ALL
        </Badge>
      );
    }

    return (
      <div
        className={`flex flex-wrap gap-1 ${isMobile ? "max-w-[180px]" : ""}`}
      >
        {normalized?.map((method, index) => {
          let label = method;
          let variant = "info";

          if (method === "PushNotification"){
            label = "PushNotification";
            variant = "success";
          } else if (method === "Push") {
            label = "SMS";
            variant = "warning";
          } else if (method === "Email") {
            variant = "info";
          } else if (method === "SMS") {
            variant = "warning";
          }

          return (
            <Badge
              key={index}
              variant={variant}
              className="mr-4 last:mr-0"
              style={{ borderRadius: "6px" }}
            >
              {label}
            </Badge>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    setNotificationData(props?.notification);
  }, [props?.notification]);

  const renderRecipients = (recipientData) => {
    if (!recipientData || recipientData?.length === 0) {
      return <span className="text-sm text-gray-400 dark:text-white">No recipients</span>;
    }

    const recipientsToShow = showAllRecipients
      ? recipientData
      : recipientData?.slice(0, 6);

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2">
          {recipientsToShow?.map((recipient, index) => (
            <div key={index} className="flex items-center gap-2">
              <User size={14} className="text-gray-500" />
              <span className="text-sm color-newBlack dark:text-white">{recipient?.name}</span>
            </div>
          ))}
        </div>

        {recipientData?.length > 6 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllRecipients(!showAllRecipients)}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            {showAllRecipients
              ? "Show less"
              : `View all ${recipientData?.length} recipients`}
          </Button>
        )}
      </div>
    );
  };

  // Updated rendering for recipient type information - now using "By Name", "By Group", "By Client"
  const getRecipientLabelAndCount = (data) => {
    if (Array.isArray(data?.Employee) && data.Employee.length > 0) {
      return {
        label: "By Name",
        count: data.Employee.length,
        recipientData: data?.Employee,
        icons: <User className="h-4 w-4 text-gray-500" />,
      };
    } else if (data?.Client && Object.keys(data.Client).length > 0) {
      return {
        label: "BY Client",
        count: 1,
        recipientData: data?.Client,
        icons: <Building className="h-4 w-4 text-gray-500" />,
      };
    } else if (data?.Group && Object.keys(data.Group).length > 0) {
      return {
        label: "By Group",
        count: 1,
        recipientData: data?.Group,
        icons: <Users className="h-4 w-4 text-gray-500" />,
      };
    }
    return { label: "", count: 0, recipientData: "", icons: "" };
  };

  const {
    label: recipientLabel,
    count: recipientCount,
    recipientData: recipientData,
    icons: icon,
  } = getRecipientLabelAndCount(notification);

  const formattedTime = moment(notification?.createdOn).format("hh:mm A");
  return (
    <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-xl dark:text-white">Notification Details</DialogTitle>
        <DialogDescription>
          Complete information about this notification
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-5 py-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium dark:text-white">
              {notification?.createdOn
                ? getFormatedDate(notification?.createdOn)
                : "-"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm dark:text-white">{formattedTime}</span>
          </div>
        </div>
        <Card className="border border-gray-100">
          <CardContent className="p-16 space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                Recipient Type:
              </h3>
              <div className="pl-4 d-flex flex-center">
                <span className="pr-4">{icon}</span>
                <span className="text-sm font-medium">{recipientLabel}: </span>
                 <span className="ml-4">{recipientLabel} ({recipientCount})</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">Type:</h3>
              <div className="flex flex-wrap gap-1 pl-4">
                <MethodBadges methods={notification?.Type} />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                Subject:
              </h3>
              <p className="text-sm pl-4 color-newBlack dark:text-white">{notification?.subject}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                Message:
              </h3>
              <p className="text-sm pl-4 break-words color-newBlack dark:text-white whitespace-pre-wrap">
                {notification?.Message}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">
                Recipients:
              </h3>
              <div className="pl-4 dark:text-white">{renderRecipients(recipientData)}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DialogContent>
  );
};

export default NotificationDetails;
